// @ts-nocheck
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import translate from "@insite/client-framework/Translate";
import { TraitValueModel, VariantTraitModel } from "@insite/client-framework/Types/ApiModels";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
// import Swatch, { SwatchTypeValues } from "@insite/mobius/Swatch"; // Valtech Custom Code
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import { injectCss } from "@insite/mobius/utilities";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import styled, { css } from "styled-components";

/* Valtech Custom Code - Start */
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import Swatch, { SwatchTypeValues } from "@store/MobiusOverrides/Swatch/Swatch";
import { linkActionStyles } from "@store/Themes/common/mixins/link";
import { swatchListItemStyles, swatchListStyles } from "@store/Themes/common/mixins/swatch";
import { productCardProps } from "@store/Themes/common/props/productCard";
import { swatchProps } from "@store/Themes/common/props/swatch";
import useSwatchSelectedAccessibleText from "@toolkit/Framework/Common/Hooks/useSwatchSelectedAccessibleText";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
/* Valtech Custom Code - End */

interface Props {
    product: ProductContextModel;
    variantTrait: VariantTraitModel;
}

export interface ProductListVariantSwatchStyles {
    container?: InjectableCss;
    swatchClickable?: ClickablePresentationProps;
    labelText?: TypographyPresentationProps;
    labelWrapper?: InjectableCss;
    productDetailLink?: LinkPresentationProps;
}

export const productListVariantSwatchStyles: ProductListVariantSwatchStyles = {
    container: productCardProps.actionsWrapper,
    swatchClickable: swatchProps.clickable,
    labelText: {
        css: css`
            display: block;
            margin-bottom: ${pxToRem(2)};
            font-size: ${x => x.theme.design.typography.size.small};
            font-weight: ${x => x.theme.design.typography.weight.bold};
        `,
    },
};

const SwatchGridDisplayItem = styled.li`
    ${swatchListItemStyles() /* Valtech Custom Code */}
`;

const SwatchGridDisplayViewMore = styled.li`
    ${swatchListItemStyles() /* Valtech Custom Code */}
    ${linkActionStyles() /* Valtech Custom Code */}
`;

export const UnorderedList = styled.ul<InjectableCss>`
    ${swatchListStyles() /* Valtech Custom Code */}
    ${injectCss}
`;

const styles = productListVariantSwatchStyles;

const ProductListVariantSwatch = ({ product, variantTrait }: Props) => {
    // Bunzl Custom Code - Start
    const history = useHistory();
    const getVariantQueryParam = (traitName: string, traitValue: string) => {
        const params = new URLSearchParams();
        params.set(traitName, traitValue);
        return params.toString();
    };

    const handleSwatchClick = (traitName: string, traitValue: string) => {
        const variantQueryParam = getVariantQueryParam(traitName, traitValue);
        const link = `${product.productInfo.productDetailPath}?${variantQueryParam}`;
        history.push(link);
    };
    // Bunzl Custom Code - End
    const swatchSelectedAccessibleText = useSwatchSelectedAccessibleText(variantTrait); // Valtech Custom Code

    const CreateSwatchGrid = (traitValue: TraitValueModel) => {
        return (
            <SwatchGridDisplayItem key={traitValue.id} title={traitValue.valueDisplay /* Valtech Custom Code */}>
                <Swatch
                    type={traitValue.swatchType as SwatchTypeValues}
                    value={
                        traitValue.swatchType === SwatchTypeValues.Image
                            ? traitValue.swatchImageValue
                            : traitValue.swatchColorValue
                    }
                    size={20}
                    borderCss={css`
                        cursor: pointer;
                    `}
                    // bunzl custom code - added onClickp
                    onClick={() => handleSwatchClick(variantTrait.name, traitValue.valueDisplay)}
                    // Valtech Removed - isDisabled
                    isSelected={traitValue.isDefault}
                    // Valtech Custom Code
                    caption={traitValue.valueDisplay}
                    hideCaption
                />
            </SwatchGridDisplayItem>
        );
    };

    const ViewMoreLink = (product: ProductContextModel) => {
        const productDetailPath = product.productInfo.productDetailPath;

        return (
            <SwatchGridDisplayViewMore data-test-selector="swatch_viewMore">
                <Link
                    {...styles.productDetailLink}
                    href={productDetailPath}
                    // Valtech Removed - alt
                    data-test-selector="swatch_ProductDescriptionLink"
                >
                    {translate("View More")}
                    {/* Valtech Custom Code - Start */}
                    <VisuallyHidden>{variantTrait.nameDisplay}</VisuallyHidden>
                    {/* Valtech Custom Code - End */}
                </Link>
            </SwatchGridDisplayViewMore>
        );
    };

    return (
        <StyledWrapper
            key={variantTrait.id}
            data-test-selector={`productList_variantSwatch_${product.product.id}`} /* Bunzl Custom Code */
            {...styles.container}
        >
            <StyledWrapper
                {...styles.labelWrapper}
                data-test-selector="productList_variantSwatch_label" /* Bunzl Custom Code */
            >
                <Typography
                    {...styles.labelText}
                    data-test-selector="productList_variantSwatch_labelText" /* Bunzl Custom Code */
                >
                    {variantTrait.nameDisplay}
                    {/* Valtech Custom Code - Start */}
                    {swatchSelectedAccessibleText && <VisuallyHidden>{swatchSelectedAccessibleText}</VisuallyHidden>}
                    {/* Valtech Custom Code - End */}
                </Typography>
            </StyledWrapper>
            <UnorderedList data-test-selector="productList_variantSwatch_swatchList" /* Bunzl Custom Code */>
                {variantTrait.traitValues
                    ?.slice(0, variantTrait.numberOfSwatchesVisible)
                    .map(traitValue => CreateSwatchGrid(traitValue))}
                {variantTrait &&
                    variantTrait.traitValues &&
                    variantTrait.numberOfSwatchesVisible < variantTrait.traitValues?.length &&
                    ViewMoreLink(product)}
            </UnorderedList>
        </StyledWrapper>
    );
};

export default ProductListVariantSwatch;
